import { useMsal } from '@azure/msal-react';
import { Button } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStores } from '../../common/hooks';
import { Notification, NotificationLocationCode } from '../../models';
import { ConfirmationDialog, CustomMessageBar, PageTitle } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { AddOrEditNotificationModal } from './components/AddOrEditNotificationModal';
import { NotificationsTable } from './components/NotificationsTable';
import './NotificationsPage.scss';

const NotificationsPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    awardStore,
    notificationPageUiStore,
  } = useStores();

  const [isLoading, setLoading] = useState<boolean>(true);
  const [isAddOrEditNotificationModalOpen, setIsAddOrEditNotificationModalOpen] = useState<boolean>(false);
  const [isConfirmRemoveDialogOpen, setIsConfirmRemoveDialogOpen] = useState<boolean>(false);

  const handleRemoveNotification = async (notification?: Notification): Promise<void> => {
    if (!notification) return;

    setLoading(true);
    try {
      await apiStore.deleteNotification(instance, notification.notificationId);
      await getNotifications();

      notificationPageUiStore.setSelectedNotification(undefined);
      setIsConfirmRemoveDialogOpen(false);
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoading(false);
    }
  }

  const onRemoveNotification = (notification: Notification): void => {
    notificationPageUiStore.setSelectedNotification(notification);
    setIsConfirmRemoveDialogOpen(true);
  }

  const onCancelRemoveNotification = (): void => {
    notificationPageUiStore.setSelectedNotification(undefined);
    setIsConfirmRemoveDialogOpen(false);
  }

  const onEditNotificationStatus = async (notificationId: number, newValue: boolean): Promise<void> => {
    if (notificationPageUiStore.isErrorToastOpen) {
      notificationPageUiStore.setIsErrorToastOpen(false);
    }

    setLoading(true);
    try {
      await apiStore.updateNotificationStatus(instance, notificationId, newValue);
      await getNotifications();
    } catch (error) {
      // TODO - handle error
    } finally {
      setLoading(false);
    }
  }

  const onAddNewNotification = (): void => {
    setIsAddOrEditNotificationModalOpen(true);
  }

  const onEditNotification = (locationCode: NotificationLocationCode, notification: Notification): void => {
    notificationPageUiStore.setSelectedNotificationLocationCode(locationCode);
    notificationPageUiStore.setSelectedNotification(notification);
    setIsAddOrEditNotificationModalOpen(true);
  }

  const onDismissAddOrEditNotificationModal = (): void => {
    setIsAddOrEditNotificationModalOpen(false);
  }

  const getNotifications = async (): Promise<void> => {
    try {
      const notifications = await apiStore.getNotifications(instance);
      notificationPageUiStore.setNotifications(notifications);
    } catch (error) {
      // TODO - handle error
    }
  }

  const preloadData = async (): Promise<void> => {
    setLoading(true);

    await Promise.allSettled([
      getNotifications(),
    ]);

    setLoading(false);
  }

  useEffect(() => {
    preloadData();
    return () => {
      notificationPageUiStore.resetStates();
    }
  }, []);

  return (
    <FlexBox
      className='notifications-page'
      aligns='start'
      direction='column'
    >
      {isLoading && <Loading />}
      {
        isAddOrEditNotificationModalOpen && (
          <AddOrEditNotificationModal
            isModalOpen={isAddOrEditNotificationModalOpen}
            selectedNotification={notificationPageUiStore.selectedNotification}
            onSuccess={() => preloadData()}
            onDismissModal={() => onDismissAddOrEditNotificationModal()}
          />
        )
      }
      {
        notificationPageUiStore.isErrorToastOpen && (
          <CustomMessageBar
            message={'You have reached the maximum number of active notifications for this location. Please deactivate an existing notification to activate this one.'}
            notificationTypeCode='error'
            showDismissButton
            onDismissClicked={() => notificationPageUiStore.setIsErrorToastOpen(false)}
          />
        )
      }
      <ConfirmationDialog
        isOpen={isConfirmRemoveDialogOpen}
        isLoading={isLoading}
        dialogTitle='Confirm Remove Notification'
        dialogSubtext={(<p className='fs-6'>Are you sure you want to remove this notification? This action cannot be undone.</p>)}
        onPrimaryButtonClick={() => handleRemoveNotification(notificationPageUiStore.selectedNotification)}
        onSecondaryButtonClick={() => onCancelRemoveNotification()}
        primaryButtonLabel='Yes'
        secondaryButtonLabel='No, go back'
        onCloseDialogClick={() => onCancelRemoveNotification()}
      />
      <PageTitle
        title='Notifications'
        subtitle={awardStore.defaultProgramRef?.displayName}
      />
      <Button
        appearance='primary'
        onClick={() => onAddNewNotification()}
      >
        + Add new
      </Button>
      <div className='notification-main-content'>
        <NotificationsTable
          title='Partner homepage'
          notificationLocationCode='partner-homepage'
          onRemoveNotification={onRemoveNotification}
          onEditNotification={onEditNotification}
          onEditNotificationStatus={onEditNotificationStatus}
        />
        <NotificationsTable
          title='Program dashboard'
          notificationLocationCode='program-dashboard'
          onRemoveNotification={onRemoveNotification}
          onEditNotification={onEditNotification}
          onEditNotificationStatus={onEditNotificationStatus}
        />
        <NotificationsTable
          title='Winner resource'
          notificationLocationCode='winner-resource'
          onRemoveNotification={onRemoveNotification}
          onEditNotification={onEditNotification}
          onEditNotificationStatus={onEditNotificationStatus}
        />
      </div>
    </FlexBox>
  );
}

export const NotificationsPage = observer(NotificationsPageImpl);